import { useAISDKRuntime } from "@assistant-ui/ai-sdk";
import { type AssistantRuntime, WebSpeechDictationAdapter } from "@assistant-ui/react";
import { useAgentChat } from "@cloudflare/ai-chat/react";
import { useAgent } from "agents/react";
import type { UIMessage } from "ai";
import { type RefObject, useEffect, useMemo, useRef, useState } from "react";
import { API_URL } from "@/lib/api-url";
import { chatAttachmentAdapter } from "@/lib/attachments";
import { useSession } from "@/lib/auth-client";
import { registerStopper } from "@/lib/run-control";

/**
 * One chat thread, live from its `ChatAgent` Durable Object (ARCHITECTURE.md → "Chat threads").
 *
 * The thread's messages, streaming and resume all go over one WebSocket (`useAgent` +
 * `useAgentChat`), so the same thread open in the new tab and the side panel stays in sync, and
 * an answer that was streaming when the tab closed is there when it opens again. Every branch
 * (edits, regenerations) is loaded from the object once and seeds assistant-ui's branch picker.
 */

/** What `GET /api/chat/agents/:id/branches` returns (assistant-ui's MessageFormatRepository). */
type BranchRepository = {
  headId: string | null;
  messages: { parentId: string | null; message: UIMessage }[];
};

const api = new URL(API_URL);
const SOCKET_HOST = api.host;
const SOCKET_PROTOCOL = api.protocol === "https:" ? "wss" : "ws";

/** A ticket opens the WebSocket for 60 s; one older than this is replaced before a reconnect. */
const TICKET_REFRESH_MS = 30_000;

async function authedFetch(path: string, token: string, init: RequestInit = {}) {
  return fetch(`${API_URL}${path}`, {
    ...init,
    credentials: "include",
    headers: { ...init.headers, Authorization: `Bearer ${token}` },
  });
}

/**
 * Runs the browser agent's tools (ADMIN-217) when the model calls one. Only the side panel has
 * one; a client with a runner tells the ChatAgent so (`browserTools` in the turn's body), and
 * only then is the model given the tools.
 */
export type ToolRunner = {
  handles: (toolName: string) => boolean;
  run: (toolName: string, input: unknown) => Promise<unknown>;
};

/** The model, effort and instructions the composer has set, sent with each turn. */
function turnBody(runtime: AssistantRuntime | null, browserTools: boolean) {
  const context = runtime?.thread.getModelContext();
  const config = context?.config as { modelName?: string; reasoningEffort?: string } | undefined;
  return {
    ...(config?.modelName ? { modelName: config.modelName } : {}),
    ...(config?.reasoningEffort ? { reasoningEffort: config.reasoningEffort } : {}),
    ...(context?.system ? { system: context.system } : {}),
    ...(browserTools ? { browserTools: true } : {}),
  };
}

/**
 * Adds to a message just before it's sent (the side panel attaches the page you're on). It gets
 * the AI SDK message the composer built and returns the one to send.
 */
export type PrepareMessage = (message: UIMessage) => Promise<UIMessage>;

type ChatHelpers = ReturnType<typeof useAgentChat>;

/** How long a message waits for the socket to open before it is sent anyway. */
const SOCKET_WAIT_MS = 20_000;
const SOCKET_OPEN = 1;

/**
 * Resolves once the thread's socket is open. The composer is usable before that: the ticket is
 * still being fetched, or the socket is reconnecting. A message sent on a socket that isn't open
 * is buffered on a socket that gets replaced when the ticket arrives, so it was silently lost and
 * no answer ever came. Waiting here keeps it until it can be delivered.
 */
async function socketOpen(agent: RefObject<{ readyState: number }>) {
  const deadline = Date.now() + SOCKET_WAIT_MS;
  while (agent.current.readyState !== SOCKET_OPEN && Date.now() < deadline) {
    await new Promise((resolve) => setTimeout(resolve, 50));
  }
}

/**
 * The chat with `sendMessage` waiting for the socket, then running `prepare` when there is one,
 * and marking the turn as started here (`sentHere`), so tool calls are only run by the client that
 * asked.
 */
function withPrepare(
  chat: ChatHelpers,
  prepare: RefObject<PrepareMessage | null> | undefined,
  agent: RefObject<{ readyState: number }>,
  sentHere: RefObject<boolean>,
) {
  const sendMessage: ChatHelpers["sendMessage"] = async (message, options) => {
    sentHere.current = true;
    await socketOpen(agent);
    const fn = prepare?.current;
    const next =
      fn && message && "parts" in message && message.parts
        ? ((await fn(message as UIMessage)) as typeof message)
        : message;
    return chat.sendMessage(next, options);
  };
  const regenerate: ChatHelpers["regenerate"] = (options) => {
    sentHere.current = true;
    return chat.regenerate(options);
  };
  return { ...chat, sendMessage, regenerate };
}

export function useAgentThread({
  threadId,
  isNew,
  prepareMessage,
  toolRunner,
}: {
  threadId: string;
  isNew: boolean;
  prepareMessage?: RefObject<PrepareMessage | null> | undefined;
  toolRunner?: RefObject<ToolRunner | null> | undefined;
}) {
  const { data: session } = useSession();
  const token = session?.session?.token as string | undefined;

  // A chat that hasn't been sent yet has no history to load; one that has loads its branches
  // before the socket opens, so a resumed answer lands after its history, not on an empty thread.
  const [repository, setRepository] = useState<BranchRepository | null | undefined>(
    isNew ? null : undefined,
  );
  // biome-ignore lint/correctness/useExhaustiveDependencies: history loads once per thread
  useEffect(() => {
    if (repository !== undefined || !token) return;
    let cancelled = false;
    authedFetch(`/api/chat/agents/${encodeURIComponent(threadId)}/branches`, token)
      .then(async (res) => (res.ok ? ((await res.json()) as BranchRepository) : null))
      .catch(() => null)
      .then((loaded) => {
        if (!cancelled) setRepository(loaded?.messages.length ? loaded : null);
      });
    return () => {
      cancelled = true;
    };
  }, [threadId, token]);

  // The WebSocket can't carry the bearer token, so it opens with a short-lived ticket.
  const [ticket, setTicket] = useState<{ value: string; issuedAt: number } | null>(null);
  useEffect(() => {
    if (ticket || !token) return;
    let cancelled = false;
    let retry: ReturnType<typeof setTimeout> | undefined;
    const request = async () => {
      try {
        const res = await authedFetch(
          `/api/chat/threads/${encodeURIComponent(threadId)}/connect`,
          token,
          { method: "POST" },
        );
        if (!res.ok) throw new Error(`connect: ${res.status}`);
        const { ticket: value } = (await res.json()) as { ticket: string };
        if (!cancelled) setTicket({ value, issuedAt: Date.now() });
      } catch (err) {
        console.error("[chat] could not open the thread", err);
        if (!cancelled) retry = setTimeout(request, 3000);
      }
    };
    request();
    return () => {
      cancelled = true;
      clearTimeout(retry);
    };
  }, [threadId, token, ticket]);
  const ticketRef = useRef(ticket);
  ticketRef.current = ticket;

  const agent = useAgent({
    agent: "chat-agent",
    name: threadId,
    host: SOCKET_HOST,
    protocol: SOCKET_PROTOCOL,
    basePath: `api/chat/agents/${threadId}`,
    query: ticket ? { ticket: ticket.value } : undefined,
    enabled: !!ticket && repository !== undefined,
    onClose: () => {
      // A reconnect reuses the ticket in the URL: swap it for a fresh one if it's getting old.
      const current = ticketRef.current;
      if (current && Date.now() - current.issuedAt > TICKET_REFRESH_MS) setTicket(null);
    },
  });

  const agentRef = useRef(agent);
  agentRef.current = agent;

  const runtimeRef = useRef<AssistantRuntime | null>(null);
  // Whether this client sent the thread's latest turn. Every client watching the thread sees a
  // tool call, but only the one that asked runs it (the new tab watching a panel's run doesn't
  // click too), and a call left over from before a reload isn't run again.
  const sentHere = useRef(false);
  const chat = useAgentChat({
    agent,
    // History comes from /branches (all of them, not just the one on screen) above.
    getInitialMessages: null,
    body: () => turnBody(runtimeRef.current, !!toolRunner?.current),
    onToolCall: async ({ toolCall, addToolOutput }) => {
      const runner = toolRunner?.current;
      if (!runner || !sentHere.current || !runner.handles(toolCall.toolName)) return;
      const output = await runner.run(toolCall.toolName, toolCall.input);
      addToolOutput({ toolCallId: toolCall.toolCallId, output });
    },
  });

  const stop = chat.stop;
  useEffect(() => registerStopper(threadId, stop), [threadId, stop]);

  const dictation = useMemo(() => new WebSpeechDictationAdapter(), []);
  const runtime = useAISDKRuntime(withPrepare(chat, prepareMessage, agentRef, sentHere), {
    adapters: { dictation, attachments: chatAttachmentAdapter },
    // Messages sent while an answer streams wait in the composer's queue (ADMIN-212).
    unstable_enableMessageQueue: true,
    ...(repository ? { messageRepository: repository } : {}),
  });
  runtimeRef.current = runtime;
  return { runtime, chat };
}
